import {MetaTags} from '@frontend/components/MetaTags'
import {useConfigureUi} from '@frontend/hooks/useConfigureUi'
import type {
    AdsListPresentation,
    FilteredItemsListPageHeaderInfo,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useInitFilteredItemsPage} from '@frontend/modules/public/ads/hooks/useInitFilteredItemsPage'
import {LocalStorage} from '@frontend/services/LocalStorage'
import clsx from 'clsx'
import {useState} from 'react'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AdsOrServicesBreadcrumbs} from './components/AdsOrServicesBreadcrumbs'
import {ItemsCategoryFiltersPanelMobile} from './components/category_filters/ItemsCategoryFiltersPanelMobile'
import {ItemsCategoryFiltersPanelTabletAndDesktop} from './components/category_filters/ItemsCategoryFiltersPanelTabletAndDesktop'
import {FilteredAdsList} from './components/FilteredAdsList'
import {AdsListActionsPanel} from './components/list/AdsListActionsPanel'

// Страница со списком объявлений и дополнительной фильтрацией.
export default function AdsPage() {

    const [
        pageHeaderInfo,
        setPageHeaderInfo,
    ] = useState<FilteredItemsListPageHeaderInfo>(() => ({
        header: '',
        totalItemsCount: 0,
        categoriesPath: [],
        isLoadedOnce: false,
    }))

    const [
        adsListPresentation,
        setAdsListPresentation,
    ] = useState<AdsListPresentation>(() => LocalStorage.getAdsListPresentation('grid'))

    const {
        header,
        categoriesPath,
        totalItemsCount,
        isLoadedOnce,
    } = pageHeaderInfo

    const locationPrefix: string | null = (
        categoriesPath[0]?.breadcrumbType === 'location'
        && header.startsWith(categoriesPath[0].name + ':')
    )
        ? categoriesPath[0].name + ':'
        : null

    // Настройка глобальных элементов интерфейса приложения.
    useConfigureUi(config => {
        config.topMenu.wrapInCard = true
        config.topMenu.stickToTop = true
        return config
    })

    // Инициализация страницы поиска объявлений.
    const isReady = useInitFilteredItemsPage()

    // Смена представления списка объявлений.
    const onAdsListPresentationChange = useEventCallback((
        value: AdsListPresentation
    ) => {
        setAdsListPresentation(value)
        LocalStorage.setAdsListPresentation(value)
    })

    if (!isReady) {
        return null
    }

    return (
        <div className="container-fluid container-xl">
            <div className="flex-container-row pb-4">
                <div className="flex-1">
                    <div>
                        {categoriesPath.length > 0 && (
                            <Collapse show={true}>
                                <AdsOrServicesBreadcrumbs
                                    path={categoriesPath}
                                    className="mb-3"
                                />
                            </Collapse>
                        )}
                        {header.length > 0 && (
                            <MetaTags
                                title={header}
                                wrapTitle
                            />
                        )}
                        {header.length > 0 && (
                            <Collapse show={true}>
                                <div
                                    className={clsx(
                                        'd-flex flex-column align-items-stretch justify-content-start',
                                        'flex-md-row align-items-md-center'
                                    )}
                                >
                                    <div className="flex-1 d-flex flex-row align-items-center justify-content-between">
                                        <div className="d-flex flex-row align-items-center justify-content-start gap-3">
                                            <h1 className="my-0 offered-items-list-page-header fs-5 fs-md-3 fw-800">
                                                {/* <h1> на странице - flex-колонка, поэтому текст в одном элементе. */}
                                                <span>
                                                    {locationPrefix && (
                                                        <span className="text-muted">{locationPrefix}</span>
                                                    )}
                                                    {locationPrefix ? header.slice(locationPrefix.length) : header}
                                                </span>
                                            </h1>
                                            <span className="fs-5 fs-md-1 fw-700 text-muted lh-1">
                                                {totalItemsCount}
                                            </span>
                                        </div>
                                    </div>
                                    <ItemsCategoryFiltersPanelMobile className="mt-3" />
                                </div>
                            </Collapse>
                        )}
                    </div>
                    <div className="flex-container-row mt-3 mt-sm-4">
                        {isLoadedOnce && (
                            <ItemsCategoryFiltersPanelTabletAndDesktop />
                        )}
                        <div className="flex-1 d-flex flex-column gap-4">
                            <AdsListActionsPanel
                                totalItemsCount={totalItemsCount}
                                adsListPresentation={adsListPresentation}
                                setAdsListPresentation={onAdsListPresentationChange}
                            />
                            <FilteredAdsList
                                key={adsListPresentation}
                                setPageHeaderInfo={setPageHeaderInfo}
                                adsPresentation={adsListPresentation}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
